import { useState, useEffect, useCallback } from "react"
import { generatePrediction } from "./predictionEngine"
import { supabase } from "./supabase"

const G = "#22C55E"
const NAVY = "#0D1B2A"
const F = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif"

const SPORT_DATA = {
  "Football":     { leagues: ["Premier League","Champions League","La Liga","Bundesliga","Serie A","Ligue 1","Europa League","FA Cup","MLS","Eredivisie","Primeira Liga"] },
  "Basketball":   { leagues: ["NBA","EuroLeague","NCAA","NBL","WNBA","G-League"] },
  "Tennis":       { leagues: ["ATP Tour","WTA Tour","Grand Slam","Davis Cup","Laver Cup","ATP Masters"] },
  "Am. Football": { leagues: ["NFL","NCAA Football","CFL","XFL"] },
  "Cricket":      { leagues: ["IPL","Test Match","ODI","T20 World Cup","The Ashes","BBL","CPL"] },
}

const PREDICTIONS_BY_SPORT = {
  "Football":     ["Home Win","Away Win","Draw","Over 2.5 Goals","Under 2.5 Goals","Both Teams to Score","First Half Over 1.5","Asian Handicap"],
  "Basketball":   ["Home Win","Away Win","Over Total","Under Total","Home -5.5","Away +5.5","First Quarter Winner"],
  "Tennis":       ["Player 1 Win","Player 2 Win","Over 22.5 Games","Under 22.5 Games","Player 1 First Set","Straight Sets"],
  "Am. Football": ["Home Win","Away Win","Over Total","Under Total","Home -3.5","Away +3.5","First Half Winner"],
  "Cricket":      ["Home Win","Away Win","Draw","Over 6.5 Match Sixes","Top Team Batsman","Toss Winner"],
}

const TIME_OPTIONS = [
  "Today · 12:00", "Today · 13:00", "Today · 14:00", "Today · 15:00",
  "Today · 16:00", "Today · 17:00", "Today · 18:00", "Today · 19:00",
  "Today · 20:00", "Today · 20:45", "Today · 21:00", "Today · 22:00",
  "Tomorrow · 12:00", "Tomorrow · 14:00", "Tomorrow · 15:00", "Tomorrow · 17:30",
  "Tomorrow · 19:00", "Tomorrow · 20:00", "Tomorrow · 20:45", "Tomorrow · 21:00",
  "Sat · 12:30", "Sat · 15:00", "Sat · 17:30", "Sat · 20:00", "Sat · 21:00",
  "Sun · 14:00", "Sun · 15:00", "Sun · 16:30", "Sun · 18:30", "Sun · 21:00",
  "Mon · 20:00", "Mon · 21:00", "Tue · 20:00", "Tue · 21:00",
  "Wed · 20:00", "Wed · 21:00", "Thu · 20:00", "Fri · 20:00",
]

const CONFIDENCE_OPTIONS = [55,60,65,68,70,72,74,75,76,77,78,80,82,84,85,88,90,92,95]
const ODDS_OPTIONS = ["1.40","1.50","1.60","1.65","1.70","1.75","1.80","1.85","1.88","1.90","1.92","1.95","1.97","2.00","2.05","2.10","2.15","2.20","2.25","2.30","2.40","2.50","2.75","3.00","3.20","3.50","4.00","4.50","5.00"]
const BADGE_OPTIONS = ["AI TOP PICK","VALUE BET","HIGH PROBABILITY","SHARP MONEY","EARLY VALUE","BANKER"]

// ── All inputs defined OUTSIDE component ──────────────────────
const Dropdown = ({ label, value, onChange, options, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, letterSpacing: "0.08em", fontFamily: F, marginBottom: 5 }}>{label}</div>
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1.5px solid #E5E7EB", fontSize: 14, color: value ? NAVY : "#9CA3AF", fontFamily: F, outline: "none", background: "#fff", boxSizing: "border-box" }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
    </select>
  </div>
)

const TextInput = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, letterSpacing: "0.08em", fontFamily: F, marginBottom: 5 }}>{label}</div>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1.5px solid #E5E7EB", fontSize: 14, color: NAVY, fontFamily: F, outline: "none", background: "#fff", boxSizing: "border-box" }} />
  </div>
)

const Badge = ({ text, color }) => (
  <span style={{ background: color + "20", color, fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 6, fontFamily: F }}>{text}</span>
)

export default function AdminPanel() {
  const [sport, setSport]           = useState("Football")
  const [league, setLeague]         = useState("")
  const [home, setHome]             = useState("")
  const [away, setAway]             = useState("")
  const [time, setTime]             = useState("")
  const [prediction, setPrediction] = useState("")
  const [confidence, setConfidence] = useState("")
  const [odds, setOdds]             = useState("")
  const [badge, setBadge]           = useState("AI TOP PICK")
  const [minTier, setMinTier]       = useState("scout")
  const [mode, setMode]             = useState("ai") // ai | manual
  const [loading, setLoading]       = useState(false)
  const [preview, setPreview]       = useState(null)
  const [saved, setSaved]           = useState(false)
  const [error, setError]           = useState("")
  const [picks, setPicks]           = useState([])
  const [tab, setTab]               = useState("new")

  useEffect(() => { loadPicks() }, [])

  // Reset prediction when sport changes
  useEffect(() => { setPrediction(""); setLeague("") }, [sport])

  const loadPicks = async () => {
    const { data } = await supabase.from("picks").select("*")
      .eq("active", true).order("created_at", { ascending: false }).limit(30)
    if (data) setPicks(data)
  }

  const handleGenerate = useCallback(async () => {
    if (!home.trim() || !away.trim() || !league) {
      setError("Please fill in League, Home team, and Away team."); return
    }
    setError(""); setLoading(true); setPreview(null); setSaved(false)
    try {
      const pred = await generatePrediction({ sport, league, home, away, time, minTier })
      setPreview(pred)
    } catch (e) {
      setError("AI generation failed. Check your Anthropic API key in Netlify.")
    }
    setLoading(false)
  }, [sport, league, home, away, time, minTier])

  const handleManualSave = useCallback(async () => {
    if (!home || !away || !league || !prediction || !confidence || !odds) {
      setError("Please fill in all required fields."); return
    }
    setLoading(true)
    const conf = parseInt(confidence)
    const o = parseFloat(odds)
    const p = conf / 100
    const ev = ((p * (o - 1)) - (1 - p)) * 100
    const evStr = ev > 0 ? `+${ev.toFixed(1)}%` : `${ev.toFixed(1)}%`
    const kelly = Math.max(0, ((o - 1) * p - (1 - p)) / (o - 1) * 25).toFixed(1)

    const { error: e } = await supabase.from("picks").insert({
      sport, league, home, away, time, prediction,
      confidence: conf, ev: evStr, odds,
      min_tier: minTier, badge, sharp: false, early: false,
      prob_h: Math.round(conf * 0.9), prob_d: Math.round((100 - conf) * 0.4),
      prob_a: Math.round((100 - conf) * 0.6),
      odds_comp: [], active: true, result: "pending"
    })
    if (e) { setError(e.message); setLoading(false); return }
    setSaved(true); setLoading(false); loadPicks()
    setTimeout(() => {
      setHome(""); setAway(""); setPrediction(""); setConfidence(""); setOdds("")
      setSaved(false)
    }, 3000)
  }, [sport, league, home, away, time, prediction, confidence, odds, badge, minTier])

  const handleAISave = useCallback(async () => {
    if (!preview) return
    setLoading(true)
    const { error: e } = await supabase.from("picks").insert({
      sport: preview.sport, league: preview.league,
      home: preview.home, away: preview.away, time: preview.time,
      prediction: preview.prediction, confidence: preview.confidence,
      ev: preview.ev, odds: preview.odds, min_tier: minTier,
      badge: preview.badge, sharp: preview.sharp, early: preview.early,
      prob_h: preview.probH, prob_d: preview.probD, prob_a: preview.probA,
      odds_comp: preview.oddsComp || [], active: true, result: "pending"
    })
    if (e) { setError(e.message); setLoading(false); return }
    setSaved(true); setLoading(false); loadPicks()
    setTimeout(() => {
      setHome(""); setAway(""); setLeague(""); setTime("")
      setPreview(null); setSaved(false)
    }, 3000)
  }, [preview, minTier])

  const handleSetResult = async (id, result) => {
    await supabase.from("picks").update({ result }).eq("id", id)
    loadPicks()
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this pick?")) return
    await supabase.from("picks").update({ active: false }).eq("id", id)
    loadPicks()
  }

  const resultColor = r => ({ won: G, lost: "#EF4444", void: "#9CA3AF", pending: "#F59E0B" })[r] || "#F59E0B"

  const leagues = SPORT_DATA[sport]?.leagues || []
  const predictions = PREDICTIONS_BY_SPORT[sport] || []

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 20px", fontFamily: F, background: "#F9FAFB", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: NAVY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>⚡</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: NAVY }}>SportOracle Admin</div>
            <div style={{ fontSize: 12, color: "#6B7280" }}>{picks.length} active picks</div>
          </div>
        </div>
        <a href="/" style={{ fontSize: 13, color: G, fontWeight: 700, textDecoration: "none" }}>← View App</a>
      </div>

      {/* Main tabs */}
      <div style={{ display: "flex", gap: 4, background: "#E5E7EB", borderRadius: 12, padding: 4, marginBottom: 24, width: "fit-content" }}>
        {[{ id: "new", label: "➕ New Pick" }, { id: "manage", label: `📊 Manage (${picks.length})` }].map(t => (
          <div key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: "8px 20px", borderRadius: 9, cursor: "pointer", fontSize: 14, fontWeight: 700,
              background: tab === t.id ? "#fff" : "transparent", color: tab === t.id ? NAVY : "#6B7280",
              boxShadow: tab === t.id ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>
            {t.label}
          </div>
        ))}
      </div>

      {/* ── NEW PICK ── */}
      {tab === "new" && (
        <div>
          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 4, background: "#E5E7EB", borderRadius: 10, padding: 4, marginBottom: 20, width: "fit-content" }}>
            {[{ id: "ai", label: "🤖 AI Generate" }, { id: "manual", label: "✏️ Manual Entry" }].map(m => (
              <div key={m.id} onClick={() => setMode(m.id)}
                style={{ padding: "7px 16px", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  background: mode === m.id ? "#fff" : "transparent", color: mode === m.id ? NAVY : "#6B7280" }}>
                {m.label}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Left: Form */}
            <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 18 }}>🏟️ Match Details</div>

              <Dropdown label="SPORT" value={sport} onChange={setSport} options={Object.keys(SPORT_DATA)} />
              <Dropdown label="LEAGUE / COMPETITION" value={league} onChange={setLeague} options={leagues} placeholder="Select league..." />
              <TextInput label="HOME TEAM" value={home} onChange={setHome} placeholder="e.g. Arsenal" />
              <TextInput label="AWAY TEAM" value={away} onChange={setAway} placeholder="e.g. PSG" />
              <Dropdown label="KICK-OFF TIME" value={time} onChange={setTime} options={TIME_OPTIONS} placeholder="Select time..." />
              <Dropdown label="ACCESS TIER" value={minTier} onChange={setMinTier}
                options={[{ value: "scout", label: "🆓 Scout (Free)" }, { value: "elite", label: "⭐ Elite Only" }]} />

              {mode === "manual" && (
                <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 16, marginTop: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 12 }}>Prediction Details</div>
                  <Dropdown label="PREDICTION" value={prediction} onChange={setPrediction} options={predictions} placeholder="Select prediction..." />
                  <Dropdown label="CONFIDENCE" value={confidence} onChange={setConfidence}
                    options={CONFIDENCE_OPTIONS.map(c => ({ value: String(c), label: c + "%" }))} placeholder="Select confidence..." />
                  <Dropdown label="BEST ODDS" value={odds} onChange={setOdds} options={ODDS_OPTIONS} placeholder="Select odds..." />
                  <Dropdown label="BADGE" value={badge} onChange={setBadge} options={BADGE_OPTIONS} />
                </div>
              )}

              {error && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#DC2626" }}>
                  {error}
                </div>
              )}

              {mode === "ai" ? (
                <div onClick={!loading ? handleGenerate : undefined}
                  style={{ background: loading ? "#9CA3AF" : G, borderRadius: 12, padding: "14px", textAlign: "center", cursor: loading ? "not-allowed" : "pointer" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                    {loading ? "🤖 AI Analysing..." : "🤖 Generate AI Prediction"}
                  </span>
                </div>
              ) : (
                <div onClick={!loading ? handleManualSave : undefined}
                  style={{ background: loading ? "#9CA3AF" : NAVY, borderRadius: 12, padding: "14px", textAlign: "center", cursor: loading ? "not-allowed" : "pointer" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                    {saved ? "✅ Published!" : loading ? "Saving..." : "✅ Publish Pick"}
                  </span>
                </div>
              )}

              {loading && mode === "ai" && (
                <div style={{ marginTop: 12, padding: "12px 16px", background: "#F0FDF4", borderRadius: 10, border: "1px solid #BBF7D0" }}>
                  <div style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 6 }}>AI working — takes 15–20 seconds</div>
                  {["🔍 Searching live odds & team news", "📊 Running probability model", "🧮 Calculating EV & Kelly", "✍️ Writing analysis"].map((s, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#15803D", marginTop: 3 }}>• {s}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Preview */}
            <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 18 }}>
                {mode === "ai" ? "📋 AI Preview" : "📋 Pick Preview"}
              </div>

              {!preview && !saved && mode === "ai" && (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#9CA3AF" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
                  <div style={{ fontSize: 14 }}>Fill in match details and click Generate</div>
                  <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.6, color: "#D1D5DB" }}>AI searches the web for live odds, team form, injuries, then generates a full prediction with EV and Kelly stake</div>
                </div>
              )}

              {mode === "manual" && !saved && (home || away) && (
                <div style={{ background: NAVY, borderRadius: 12, padding: 16, color: "#fff", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>{league?.toUpperCase() || "LEAGUE"}</div>
                  <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 10 }}>{home || "Home"} vs {away || "Away"}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {prediction && <Badge text={prediction} color={G} />}
                    {odds && <Badge text={"Odds: " + odds} color="#fff" />}
                    {confidence && <Badge text={confidence + "% confidence"} color="#F59E0B" />}
                  </div>
                </div>
              )}

              {preview && !saved && (
                <div>
                  <div style={{ background: NAVY, borderRadius: 12, padding: 16, marginBottom: 14, color: "#fff" }}>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>{preview.league?.toUpperCase()}</div>
                    <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 10 }}>{preview.home} vs {preview.away}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Badge text={preview.prediction} color={G} />
                      <Badge text={"Odds: " + preview.odds} color="#fff" />
                      <Badge text={"EV: " + preview.ev} color={G} />
                      <Badge text={"Kelly: " + preview.kelly + "%"} color="#F59E0B" />
                    </div>
                  </div>

                  {[{ label: preview.home, pct: preview.probH }, { label: "Draw", pct: preview.probD }, { label: preview.away, pct: preview.probA }].map((row, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ width: 90, fontSize: 12, color: NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.label}</div>
                      <div style={{ flex: 1, height: 7, background: "#F3F4F6", borderRadius: 4 }}>
                        <div style={{ width: (row.pct || 0) + "%", height: "100%", background: G, borderRadius: 4 }} />
                      </div>
                      <div style={{ width: 32, fontSize: 12, fontWeight: 700, color: NAVY, textAlign: "right" }}>{row.pct}%</div>
                    </div>
                  ))}

                  {preview.analysis && (
                    <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: 14, margin: "14px 0" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: G, marginBottom: 6 }}>AI ANALYSIS</div>
                      <div style={{ fontSize: 13, color: NAVY, lineHeight: 1.7 }}>{preview.analysis}</div>
                    </div>
                  )}

                  {preview.keyFactors?.map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
                      <span style={{ color: G, fontWeight: 700 }}>✓</span>
                      <span style={{ fontSize: 13, color: NAVY }}>{f}</span>
                    </div>
                  ))}

                  <div onClick={!loading ? handleAISave : undefined}
                    style={{ background: NAVY, borderRadius: 12, padding: "14px", textAlign: "center", cursor: "pointer", marginTop: 16 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>✅ Save & Publish to App</span>
                  </div>
                </div>
              )}

              {saved && (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: G }}>Pick is live!</div>
                  <div style={{ fontSize: 13, color: "#6B7280", marginTop: 8 }}>Resetting in 3 seconds...</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MANAGE PICKS ── */}
      {tab === "manage" && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>Active Picks ({picks.length})</div>
            <div onClick={loadPicks} style={{ fontSize: 13, color: G, fontWeight: 700, cursor: "pointer" }}>↻ Refresh</div>
          </div>

          {picks.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px", color: "#9CA3AF" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              <div>No active picks yet.</div>
            </div>
          )}

          {picks.map(p => (
            <div key={p.id} style={{ padding: "14px 0", borderBottom: "1px solid #F3F4F6" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{p.home} vs {p.away}</span>
                    <Badge text={p.min_tier === "elite" ? "⭐ Elite" : "🆓 Free"} color={p.min_tier === "elite" ? "#F59E0B" : G} />
                    <Badge text={p.result?.toUpperCase() || "PENDING"} color={resultColor(p.result)} />
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>
                    {p.league} · {p.prediction} · {p.confidence}% · {p.odds} · {p.time}
                  </div>
                </div>
                <div onClick={() => handleDelete(p.id)}
                  style={{ color: "#EF4444", fontSize: 12, fontWeight: 700, cursor: "pointer", marginLeft: 16, flexShrink: 0 }}>
                  Remove
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#9CA3AF" }}>Set result:</span>
                {["pending", "won", "lost", "void"].map(r => (
                  <div key={r} onClick={() => handleSetResult(p.id, r)}
                    style={{ padding: "4px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
                      background: p.result === r ? resultColor(r) : "#F3F4F6",
                      color: p.result === r ? "#fff" : "#6B7280" }}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
